/**
 * ActivityCard.tsx — the unified activity feed as a dotted timeline, with
 * the go-to links as its footer (v2.1 visual redesign, 2026-08-05).
 */
import { Box, Chip, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import type { ActivityItem } from '../../types/dashboard';

const KIND: Record<ActivityItem['kind'], { color: string; label: string }> = {
  quote: { color: '#546E96', label: 'Quote' },
  intake: { color: '#E65100', label: 'Intake' },
  test: { color: '#4A6741', label: 'Test' },
};

function timeLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (days < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export const ActivityCard = ({ activity, isAdmin }: { activity: ActivityItem[]; isAdmin: boolean }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const goTo = [
    { label: 'Projects', to: ROUTES.PROJECTS },
    { label: 'Quotes', to: ROUTES.QUOTES },
    { label: 'Work Orders', to: ROUTES.WORK_ORDERS_TAB },
    { label: 'Tests', to: ROUTES.TESTS },
    { label: 'My Schedule', to: `${ROUTES.SCHEDULE}/my-schedule` },
    ...(isAdmin ? [{ label: 'Admin', to: ROUTES.ADMIN_USERS }] : []),
  ];

  return (
    <Paper elevation={0} sx={{ borderRadius: '18px', border: '1px solid', borderColor: 'border.color', p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>
        Latest in the lab
      </Typography>
      <Box sx={{ flex: 1, position: 'relative', pl: 0.5 }}>
        {activity.length === 0 && (
          <Typography variant="caption" color="text.secondary">Quiet so far.</Typography>
        )}
        {activity.slice(0, 9).map((a, i, arr) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.25, position: 'relative', pb: i < arr.length - 1 ? 1.1 : 0 }}>
            {/* timeline dot + connector */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.4 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: KIND[a.kind].color, flexShrink: 0 }} />
              {i < arr.length - 1 && <Box sx={{ width: 2, flex: 1, backgroundColor: alpha(KIND[a.kind].color, 0.15), mt: 0.4 }} />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.3 }} noWrap>
                <b>{a.description}</b>
              </Typography>
              <Typography sx={{ fontSize: '0.67rem', color: 'text.secondary' }} noWrap>
                {timeLabel(a.timestamp)}{a.detail ? ` · ${a.detail}` : ''}{a.user ? ` — ${a.user}` : ''}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 1.5, mt: 1, borderTop: `1px solid ${alpha('#000', 0.05)}` }}>
        {goTo.map((g) => (
          <Chip
            key={g.label} label={g.label} size="small" clickable onClick={() => navigate(g.to)}
            sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700, backgroundColor: alpha(theme.palette.primary.main, 0.06), color: theme.palette.primary.main }}
          />
        ))}
      </Box>
    </Paper>
  );
};
